import { useEffect, useState } from 'react'
import { useBacktest } from '@/hooks/useBacktest'
import type { BacktestConfig } from '@/lib/types'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import {
  FlaskConical, Play, TrendingUp, TrendingDown, BarChart2,
  Target, AlertTriangle, Trophy, Loader2, Award,
  type LucideIcon,
} from 'lucide-react'
import { clsx } from 'clsx'
import BacktestTabs from '@/components/ui/BacktestTabs'
import PageHeader from '@/components/ui/PageHeader'

// ── Helpers ──────────────────────────────────────────────────────────────────

function pct(n: number | null | undefined) { const v = (n == null || !Number.isFinite(n)) ? 0 : n; return `${v >= 0 ? '+' : ''}${v.toFixed(2)}%` }
function usd(n: number | null | undefined) { const v = (n == null || !Number.isFinite(n)) ? 0 : n; return `$${v.toLocaleString('en-US', { maximumFractionDigits: 0 })}` }

function KpiCard({ label, value, sub, positive, icon: Icon, hero }: {
  label: string; value: string; sub?: string; positive?: boolean
  icon?: LucideIcon; hero?: boolean
}) {
  return (
    <div className="stat-card">
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon size={14} className="text-text-muted" />}
        <p className="stat-label">{label}</p>
      </div>
      <p className={clsx(hero ? 'stat-hero' : 'stat-value text-xl', positive === true ? 'text-emerald' : positive === false ? 'text-rose-light' : '')}>
        {value}
      </p>
      {sub && <p className="text-xs text-text-muted mt-0.5">{sub}</p>}
    </div>
  )
}

// ── Portfolio Backtest Card ──────────────────────────────────────────────────
// Runs CURRENT portfolio weights through a chosen window vs benchmarks.
// New addition complementing the strategy-on-tickers config below.

function PortfolioBacktestCard() {
  const [period, setPeriod] = useState<string>('1y')
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<import('@/lib/api').PortfolioBacktestResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleRun = async () => {
    setRunning(true)
    setError(null)
    try {
      const { backtestPortfolio } = await import('@/lib/api')
      const r = await backtestPortfolio(period)
      // Backend soft-errors with {error, period} (e.g. "No priceable
      // holdings") instead of throwing — render the message, don't crash.
      const softError = (r as unknown as { error?: string })?.error
      if (softError) {
        setError(String(softError))
        setResult(null)
      } else {
        setResult(r)
      }
    } catch (e: unknown) {
      const msg = e && typeof e === 'object' && 'message' in e
        ? String((e as { message?: string }).message ?? e)
        : String(e)
      setError(msg)
    } finally {
      setRunning(false)
    }
  }

  return (
    <div className="card p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <p className="panel-title flex items-center gap-2">
            <Trophy size={14} className="text-emerald" />
            Portfolio Backtest
          </p>
          <p className="text-xs text-text-muted mt-0.5">
            Run your current weights through history vs SPY / QQQ / VUAG.L
          </p>
        </div>
        <div className="glass rounded-xl p-1.5 flex items-center gap-2">
          <select
            value={period}
            onChange={e => setPeriod(e.target.value)}
            className="input w-auto px-2 py-1.5 text-xs"
            aria-label="Backtest window"
          >
            <option value="1m">1 month</option>
            <option value="3m">3 months</option>
            <option value="6m">6 months</option>
            <option value="1y">1 year</option>
            <option value="2y">2 years</option>
            <option value="5y">5 years</option>
          </select>
          <button
            type="button"
            onClick={handleRun}
            disabled={running}
            className="btn-primary text-xs gap-1.5"
          >
            {running ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
            {running ? 'Running…' : 'Run'}
          </button>
        </div>
      </div>

      {error && (
        <p className="text-xs text-rose-light bg-rose/10 p-2 rounded">Error: {error}</p>
      )}

      {result && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard
              label="Portfolio return"
              value={pct(result.portfolio_ret_pct)}
              positive={(result.portfolio_ret_pct ?? 0) >= 0}
            />
            <KpiCard
              label="Coverage"
              value={`${(result.coverage_pct ?? 0).toFixed(0)}%`}
              sub={`£${(result.tracked_value_gbp ?? 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`}
            />
            {(result.benchmarks ?? []).slice(0, 2).map(b => (
              <KpiCard
                key={b.symbol}
                label={`vs ${b.symbol}`}
                value={b.ret_pct == null ? '—' : pct(b.ret_pct)}
                sub={
                  b.edge_pct != null
                    ? `edge ${(b.edge_pct ?? 0) >= 0 ? '+' : ''}${(b.edge_pct ?? 0).toFixed(2)}pp`
                    : undefined
                }
                positive={b.edge_pct != null ? b.edge_pct >= 0 : undefined}
              />
            ))}
          </div>

          {(result.per_ticker ?? []).length > 0 && (
            <div>
              <p className="panel-title mb-2">Top contributors</p>
              <div className="table-responsive">
                <table className="table-pro w-full text-xs">
                  <thead>
                    <tr>
                      <th>Symbol</th>
                      <th className="!text-right">Weight</th>
                      <th className="!text-right">Return</th>
                      <th className="!text-right">Contribution</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(result.per_ticker ?? []).slice(0, 10).map(t => (
                      <tr key={t.symbol}>
                        <td className="font-mono text-text-primary">{t.symbol}</td>
                        <td className="text-right text-text-secondary">{(t.weight ?? 0).toFixed(1)}%</td>
                        <td className="text-right">
                          {t.ret_pct == null
                            ? <span className="text-text-muted">—</span>
                            : <span className={t.ret_pct >= 0 ? 'delta-chip-pos' : 'delta-chip-neg'}>{pct(t.ret_pct)}</span>}
                        </td>
                        <td className="text-right">
                          {t.contribution_pct == null
                            ? <span className="text-text-muted">—</span>
                            : <span className={t.contribution_pct >= 0 ? 'delta-chip-pos' : 'delta-chip-neg'}>{pct(t.contribution_pct)}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result.skipped_no_symbol_count > 0 && (
            <p className="text-[10px] text-text-muted">
              {result.skipped_no_symbol_count} ISIN-only holdings excluded (no historical-price path).
            </p>
          )}
        </div>
      )}
    </div>
  )
}


// ── Strategy Compare Card ────────────────────────────────────────────────────
// Run all built-in strategies on a ticker set, ranked by Sharpe.

function StrategyCompareCard() {
  const [tickerInput, setTickerInput] = useState('NVDA')
  const [period, setPeriod] = useState<string>('1y')
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<import('@/lib/api').StrategyCompareResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleRun = async () => {
    const tickers = tickerInput.split(',').map(t => t.trim().toUpperCase()).filter(Boolean)
    if (!tickers.length) {
      setError('Need at least one ticker')
      return
    }
    setRunning(true)
    setError(null)
    try {
      const periodToDates: Record<string, [string, string]> = {
        '6m': ['2025-04-01', '2025-12-31'],
        '1y': ['2025-01-01', '2025-12-31'],
        '2y': ['2024-01-01', '2025-12-31'],
        '5y': ['2021-01-01', '2025-12-31'],
      }
      const [start, end] = periodToDates[period] ?? periodToDates['1y']
      const { compareStrategies } = await import('@/lib/api')
      const r = await compareStrategies({
        tickers, start_date: start, end_date: end,
        initial_capital: 10000, strategy: 'momentum',
        max_position_pct: 0.10, stop_loss_pct: 0.05, take_profit_pct: 0.15,
      })
      setResult(r)
    } catch (e: unknown) {
      setError(e && typeof e === 'object' && 'message' in e ? String((e as { message?: string }).message) : String(e))
    } finally {
      setRunning(false)
    }
  }

  return (
    <div className="card p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <p className="panel-title flex items-center gap-2">
            <Award size={14} className="text-amber-light" />
            Strategy Compare
          </p>
          <p className="text-xs text-text-muted mt-0.5">
            Run all strategies on the same tickers, ranked by Sharpe.
          </p>
        </div>
        <div className="glass rounded-xl p-1.5 flex items-center gap-2 flex-wrap">
          <input
            type="text"
            value={tickerInput}
            onChange={e => setTickerInput(e.target.value)}
            placeholder="NVDA, AAPL"
            className="input w-32 px-2 py-1.5 text-xs"
            aria-label="Tickers"
          />
          <select
            value={period}
            onChange={e => setPeriod(e.target.value)}
            className="input w-auto px-2 py-1.5 text-xs"
            aria-label="Window"
          >
            <option value="6m">6m</option>
            <option value="1y">1y</option>
            <option value="2y">2y</option>
            <option value="5y">5y</option>
          </select>
          <button
            type="button"
            onClick={handleRun}
            disabled={running}
            className="btn-primary text-xs gap-1.5"
          >
            {running ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
            {running ? 'Running…' : 'Compare'}
          </button>
        </div>
      </div>

      {error && (
        <p className="text-xs text-rose-light bg-rose/10 p-2 rounded">Error: {error}</p>
      )}

      {/* All strategies failed — surface it instead of rendering nothing */}
      {result && (result.ranked ?? []).length === 0 && (
        <p className="text-xs text-rose-light bg-rose/10 p-2 rounded">
          {(result.failed ?? []).length > 0
            ? `All ${(result.failed ?? []).length} strategies failed. First error: ${result.failed[0]?.error ?? 'unknown'}`
            : 'No strategy produced a result for these tickers/window.'}
        </p>
      )}

      {result && result.ranked.length > 0 && (
        <div className="space-y-2">
          {(result.failed ?? []).length > 0 && (
            <p className="text-[11px] text-amber-light">
              {result.failed.length} strateg{result.failed.length === 1 ? 'y' : 'ies'} failed and {result.failed.length === 1 ? 'is' : 'are'} excluded from the ranking.
            </p>
          )}
          {result.winner && (
            <div className="text-xs text-emerald-light bg-emerald/10 px-3 py-2 rounded">
              <span className="font-semibold">Winner: {result.winner.name}</span>
              {' '}— {pct(result.winner.total_return_pct)} return / Sharpe {(result.winner.sharpe_ratio ?? 0).toFixed(2)} / {result.winner.total_trades} trades
            </div>
          )}
          <div className="table-responsive">
            <table className="table-pro w-full text-xs">
              <thead>
                <tr>
                  <th>Strategy</th>
                  <th className="!text-right">Return</th>
                  <th className="!text-right">Sharpe</th>
                  <th className="!text-right">DD</th>
                  <th className="!text-right">Win%</th>
                  <th className="!text-right">Trades</th>
                </tr>
              </thead>
              <tbody>
                {result.ranked.map((r, i) => (
                  <tr key={r.strategy}>
                    <td className="text-text-primary">
                      {i === 0 && <span className="text-amber-light mr-1">★</span>}
                      {r.strategy}
                    </td>
                    <td className="text-right">
                      <span className={r.total_return_pct >= 0 ? 'delta-chip-pos' : 'delta-chip-neg'}>
                        {pct(r.total_return_pct)}
                      </span>
                    </td>
                    <td className="text-right font-mono text-text-primary">
                      {(r.sharpe_ratio ?? 0).toFixed(2)}
                    </td>
                    <td className="text-right font-mono text-rose-light">
                      {(r.max_drawdown_pct ?? 0).toFixed(1)}%
                    </td>
                    <td className="text-right text-text-secondary">{(r.win_rate ?? 0).toFixed(1)}%</td>
                    <td className="text-right text-text-secondary">{r.total_trades}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}


// ── Optimize Card ────────────────────────────────────────────────────────────
// Grid-search optimal stop/TP/position for a strategy on a ticker.

function OptimizeCard() {
  const [tickerInput, setTickerInput] = useState('NVDA')
  const [strategyName, setStrategyName] = useState('momentum')
  const [period, setPeriod] = useState<string>('1y')
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<import('@/lib/api').BacktestOptimizeResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleRun = async () => {
    const tickers = tickerInput.split(',').map(t => t.trim().toUpperCase()).filter(Boolean)
    if (!tickers.length) { setError('Need at least one ticker'); return }
    setRunning(true)
    setError(null)
    try {
      const periodToDates: Record<string, [string, string]> = {
        '6m': ['2025-04-01', '2025-12-31'],
        '1y': ['2025-01-01', '2025-12-31'],
        '2y': ['2024-01-01', '2025-12-31'],
        '5y': ['2021-01-01', '2025-12-31'],
      }
      const [start, end] = periodToDates[period] ?? periodToDates['1y']
      const { optimizeStrategy } = await import('@/lib/api')
      const r = await optimizeStrategy({
        tickers, start_date: start, end_date: end,
        initial_capital: 10000, strategy: strategyName,
        max_position_pct: 0.10, stop_loss_pct: 0.05, take_profit_pct: 0.15,
      })
      setResult(r)
    } catch (e: unknown) {
      setError(e && typeof e === 'object' && 'message' in e ? String((e as { message?: string }).message) : String(e))
    } finally {
      setRunning(false)
    }
  }

  return (
    <div className="card p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <p className="panel-title flex items-center gap-2">
            <Target size={14} className="text-blue" />
            Optimize Parameters
          </p>
          <p className="text-xs text-text-muted mt-0.5">
            Grid-search 27 stop / take-profit / position-size combos. ~30s.
          </p>
        </div>
        <div className="glass rounded-xl p-1.5 flex items-center gap-2 flex-wrap">
          <input
            type="text"
            value={tickerInput}
            onChange={e => setTickerInput(e.target.value)}
            placeholder="NVDA"
            className="input w-24 px-2 py-1.5 text-xs"
            aria-label="Ticker"
          />
          <select
            value={strategyName}
            onChange={e => setStrategyName(e.target.value)}
            className="input w-auto px-2 py-1.5 text-xs"
            aria-label="Strategy"
          >
            <option value="momentum">momentum</option>
            <option value="mean_reversion">mean_reversion</option>
            <option value="breakout">breakout</option>
            <option value="rsi">rsi</option>
            <option value="agent_signals">agent_signals</option>
            <option value="ema_crossover">ema_crossover</option>
            <option value="bollinger_reversion">bollinger_reversion</option>
            <option value="momentum_volume">momentum_volume</option>
          </select>
          <select
            value={period}
            onChange={e => setPeriod(e.target.value)}
            className="input w-auto px-2 py-1.5 text-xs"
            aria-label="Window"
          >
            <option value="6m">6m</option>
            <option value="1y">1y</option>
            <option value="2y">2y</option>
            <option value="5y">5y</option>
          </select>
          <button
            type="button"
            onClick={handleRun}
            disabled={running}
            className="btn-primary text-xs gap-1.5"
          >
            {running ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
            {running ? 'Running…' : 'Optimize'}
          </button>
        </div>
      </div>

      {error && (
        <p className="text-xs text-rose-light bg-rose/10 p-2 rounded">Error: {error}</p>
      )}

      {result && result.top.length > 0 && (
        <div className="space-y-2">
          {result.winner && (
            <div className="text-xs text-blue-light bg-blue/10 px-3 py-2 rounded">
              <span className="font-semibold">Winner:</span>{' '}
              stop {(result.winner.stop_loss_pct * 100).toFixed(0)}% / TP {(result.winner.take_profit_pct * 100).toFixed(0)}% / position {(result.winner.max_position_pct * 100).toFixed(0)}%
              {' '}→ Sharpe {(result.winner.sharpe_ratio ?? 0).toFixed(2)}, {pct(result.winner.total_return_pct)}
              <span className="text-text-muted ml-3">median Sharpe across grid {(result.median_sharpe ?? 0).toFixed(2)}</span>
            </div>
          )}
          <div className="table-responsive">
            <table className="table-pro w-full text-xs">
              <thead>
                <tr>
                  <th className="!text-right">Stop</th>
                  <th className="!text-right">TP</th>
                  <th className="!text-right">Pos</th>
                  <th className="!text-right">Return</th>
                  <th className="!text-right">Sharpe</th>
                  <th className="!text-right">DD</th>
                  <th className="!text-right">Trades</th>
                </tr>
              </thead>
              <tbody>
                {result.top.map((r, i) => (
                  <tr key={i}>
                    <td className="text-right text-text-primary">{(r.stop_loss_pct * 100).toFixed(0)}%</td>
                    <td className="text-right text-text-primary">{(r.take_profit_pct * 100).toFixed(0)}%</td>
                    <td className="text-right text-text-primary">{(r.max_position_pct * 100).toFixed(0)}%</td>
                    <td className="text-right">
                      <span className={r.total_return_pct >= 0 ? 'delta-chip-pos' : 'delta-chip-neg'}>{pct(r.total_return_pct)}</span>
                    </td>
                    <td className="text-right font-mono text-text-primary">{(r.sharpe_ratio ?? 0).toFixed(2)}</td>
                    <td className="text-right font-mono text-rose-light">{(r.max_drawdown_pct ?? 0).toFixed(1)}%</td>
                    <td className="text-right text-text-secondary">{r.total_trades}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}


// ── Default config ───────────────────────────────────────────────────────────

const DEFAULT_CONFIG: BacktestConfig = {
  tickers: ['AAPL'],
  start_date: '2024-01-01',
  end_date: '2025-12-31',
  initial_capital: 5000,
  strategy: 'momentum',
  max_position_pct: 0.10,
  stop_loss_pct: 0.05,
  take_profit_pct: 0.15,
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function BacktestPage() {
  const { result, strategies, presets, running, error, run, fetchStrategies, fetchPresets, clear } = useBacktest()
  const [config, setConfig] = useState<BacktestConfig>({ ...DEFAULT_CONFIG })
  const [tickerInput, setTickerInput] = useState('AAPL')

  useEffect(() => {
    fetchStrategies()
    fetchPresets()
  }, [fetchStrategies, fetchPresets])

  const handleRun = () => {
    const tickers = tickerInput.split(',').map(t => t.trim().toUpperCase()).filter(Boolean)
    run({ ...config, tickers })
  }

  const applyPreset = (presetConfig: BacktestConfig) => {
    setConfig(presetConfig)
    setTickerInput(presetConfig.tickers.join(', '))
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <PageHeader
        title="Strategy Backtester"
        icon={FlaskConical}
        subtitle="Test trading strategies against historical data before risking real capital"
        actions={result && (
          <button
            type="button"
            onClick={clear}
            className="btn-secondary text-xs"
          >
            Clear Results
          </button>
        )}
      />

      <BacktestTabs />

      {/* Portfolio backtest — runs CURRENT weights vs benchmarks */}
      <PortfolioBacktestCard />

      {/* Strategy compare — rank all strategies on a ticker set */}
      <StrategyCompareCard />

      {/* Optimize — grid-search stop/TP/position for one strategy */}
      <OptimizeCard />

      {/* Preset buttons */}
      {presets.length > 0 && (
        <div className="glass rounded-xl px-4 py-3">
          <p className="panel-title mb-3">Quick Presets</p>
          <div className="flex flex-wrap gap-2">
            {presets.map(p => (
              <button
                type="button"
                key={p.id}
                onClick={() => applyPreset(p.config)}
                className="badge-blue hover-lift cursor-pointer"
                title={p.description}
              >
                {p.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Config form */}
      <div className="card p-5">
        <p className="panel-title mb-4">Backtest Configuration</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Tickers */}
          <div>
            <label className="block text-xs text-text-muted mb-1">Tickers (comma-separated)</label>
            <input
              type="text"
              value={tickerInput}
              onChange={e => setTickerInput(e.target.value)}
              className="input"
              placeholder="AAPL, MSFT, NVDA"
            />
          </div>

          {/* Strategy */}
          <div>
            <label className="block text-xs text-text-muted mb-1">Strategy</label>
            <select
              value={config.strategy}
              onChange={e => setConfig(c => ({ ...c, strategy: e.target.value }))}
              aria-label="Strategy"
              className="input"
            >
              {strategies.length > 0
                ? strategies.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))
                : ['momentum', 'mean_reversion', 'breakout', 'rsi', 'agent_signals'].map(s => (
                    <option key={s} value={s}>{s.replace('_', ' ')}</option>
                  ))
              }
            </select>
          </div>

          {/* Start date */}
          <div>
            <label className="block text-xs text-text-muted mb-1">Start Date</label>
            <input
              type="date"
              value={config.start_date}
              onChange={e => setConfig(c => ({ ...c, start_date: e.target.value }))}
              title="Start Date"
              className="input"
            />
          </div>

          {/* End date */}
          <div>
            <label className="block text-xs text-text-muted mb-1">End Date</label>
            <input
              type="date"
              value={config.end_date}
              onChange={e => setConfig(c => ({ ...c, end_date: e.target.value }))}
              title="End Date"
              className="input"
            />
          </div>

          {/* Capital */}
          <div>
            <label className="block text-xs text-text-muted mb-1">Initial Capital ($)</label>
            <input
              type="number"
              value={config.initial_capital}
              onChange={e => setConfig(c => ({ ...c, initial_capital: Number(e.target.value) }))}
              title="Initial Capital"
              className="input"
              min={100}
              step={100}
            />
          </div>

          {/* Max Position */}
          <div>
            <label className="block text-xs text-text-muted mb-1">Max Position (%)</label>
            <input
              type="number"
              value={Math.round(config.max_position_pct * 100)}
              onChange={e => setConfig(c => ({ ...c, max_position_pct: Number(e.target.value) / 100 }))}
              title="Max Position %"
              className="input"
              min={1}
              max={100}
            />
          </div>

          {/* Stop Loss */}
          <div>
            <label className="block text-xs text-text-muted mb-1">Stop Loss (%)</label>
            <input
              type="number"
              value={Math.round(config.stop_loss_pct * 100)}
              onChange={e => setConfig(c => ({ ...c, stop_loss_pct: Number(e.target.value) / 100 }))}
              title="Stop Loss %"
              className="input"
              min={1}
              max={50}
            />
          </div>

          {/* Take Profit */}
          <div>
            <label className="block text-xs text-text-muted mb-1">Take Profit (%)</label>
            <input
              type="number"
              value={Math.round(config.take_profit_pct * 100)}
              onChange={e => setConfig(c => ({ ...c, take_profit_pct: Number(e.target.value) / 100 }))}
              title="Take Profit %"
              className="input"
              min={1}
              max={100}
            />
          </div>
        </div>

        {/* Strategy description */}
        {strategies.length > 0 && (
          <p className="text-xs text-text-muted mt-3">
            {strategies.find(s => s.id === config.strategy)?.description}
          </p>
        )}

        {/* Run button */}
        <div className="mt-5 flex items-center gap-3">
          <button
            type="button"
            onClick={handleRun}
            disabled={running || !tickerInput.trim()}
            className={clsx('btn-primary', running && 'cursor-wait')}
          >
            {running ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
            {running ? 'Running Backtest...' : 'Run Backtest'}
          </button>
          {error && (
            <p className="text-sm text-rose-light flex items-center gap-1">
              <AlertTriangle size={14} /> {error}
            </p>
          )}
        </div>
      </div>

      {/* Results */}
      {result && (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger-children">
            <KpiCard
              label="Total Return"
              value={pct(result.total_return_pct)}
              sub={`${usd(result.initial_capital)} -> ${usd(result.final_value)}`}
              positive={result.total_return_pct > 0}
              icon={result.total_return_pct >= 0 ? TrendingUp : TrendingDown}
              hero
            />
            <KpiCard
              label="Sharpe Ratio"
              value={(result.sharpe_ratio ?? 0).toFixed(2)}
              sub={result.sharpe_ratio >= 1 ? 'Strong' : result.sharpe_ratio >= 0.5 ? 'Moderate' : 'Weak'}
              positive={result.sharpe_ratio >= 1}
              icon={BarChart2}
            />
            <KpiCard
              label="Max Drawdown"
              value={`-${(result.max_drawdown_pct ?? 0).toFixed(2)}%`}
              sub={result.max_drawdown_pct < 10 ? 'Low risk' : result.max_drawdown_pct < 20 ? 'Moderate' : 'High'}
              positive={result.max_drawdown_pct < 10 ? true : result.max_drawdown_pct < 20 ? undefined : false}
              icon={AlertTriangle}
            />
            <KpiCard
              label="Win Rate"
              value={`${(result.win_rate ?? 0).toFixed(1)}%`}
              sub={`${result.total_trades} trades`}
              positive={result.win_rate >= 50}
              icon={Target}
            />
          </div>

          {/* Secondary KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard
              label="Annualised Return"
              value={pct(result.annualized_return)}
              positive={result.annualized_return > 0}
            />
            <KpiCard
              label="Profit Factor"
              value={(result.profit_factor ?? 0).toFixed(2)}
              sub={result.profit_factor >= 1.5 ? 'Profitable' : result.profit_factor >= 1 ? 'Marginal' : 'Losing'}
              positive={result.profit_factor >= 1.5}
            />
            <KpiCard
              label="Avg Winner"
              value={pct(result.avg_winner)}
              positive={true}
              icon={Trophy}
            />
            <KpiCard
              label="Avg Loser"
              value={pct(result.avg_loser)}
              positive={false}
            />
          </div>

          {/* Equity curve */}
          {result.equity_curve.length > 0 && (
            <div className="card p-5">
              <p className="panel-title mb-4">Equity Curve</p>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={result.equity_curve}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10, fill: '#94a3b8' }}
                      tickFormatter={v => v.slice(5)}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: '#94a3b8' }}
                      tickFormatter={v => `$${(v / 1000).toFixed(1)}k`}
                      domain={['auto', 'auto']}
                    />
                    <Tooltip
                      contentStyle={{ backgroundColor: 'rgb(var(--bg-surface))', border: '1px solid #334155', borderRadius: 8, fontSize: 12, color: 'rgb(var(--text-primary))'}}
                      labelStyle={{ color: '#94a3b8' }}
                      formatter={(v: number) => [`$${v.toLocaleString()}`, 'Portfolio']}
                    />
                    <Line
                      type="monotone"
                      dataKey="portfolio_value"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Trade log */}
          {result.trades.length > 0 && (
            <div className="card p-5">
              <p className="panel-title mb-4">
                Trade Log ({result.trades.length} trades)
              </p>
              <div className="table-responsive overflow-x-auto max-h-96">
                <table className="table-pro w-full text-xs">
                  <thead>
                    <tr>
                      <th>Ticker</th>
                      <th>Entry</th>
                      <th>Exit</th>
                      <th className="!text-right">Entry $</th>
                      <th className="!text-right">Exit $</th>
                      <th className="!text-right">Shares</th>
                      <th className="!text-right">P&L</th>
                      <th className="!text-right">P&L %</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.trades.map((t, i) => (
                      <tr key={i}>
                        <td className="font-medium text-text-primary">{t.ticker}</td>
                        <td className="text-text-secondary">{t.entry_date}</td>
                        <td className="text-text-secondary">{t.exit_date}</td>
                        <td className="text-right text-text-secondary">${(t.entry_price ?? 0).toFixed(2)}</td>
                        <td className="text-right text-text-secondary">${t.exit_price.toFixed(2)}</td>
                        <td className="text-right text-text-secondary">{t.shares}</td>
                        <td className={clsx('text-right font-medium', t.pnl >= 0 ? 'text-emerald' : 'text-rose-light')}>
                          ${t.pnl.toFixed(2)}
                        </td>
                        <td className="text-right">
                          <span className={t.pnl_pct >= 0 ? 'delta-chip-pos' : 'delta-chip-neg'}>
                            {t.pnl_pct >= 0 ? '+' : ''}{t.pnl_pct.toFixed(2)}%
                          </span>
                        </td>
                        <td>
                          <span className={clsx(
                            t.exit_reason === 'take_profit' ? 'badge-green' :
                            t.exit_reason === 'stop_loss' ? 'badge-red' :
                            t.exit_reason === 'signal' ? 'badge-blue' :
                            'badge-amber'
                          )}>
                            {t.exit_reason.replace('_', ' ')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
